import type { I18nConfig, Locale } from './i18n.ts'
import type { SiteConfig } from './site.ts'

/**
 * Runtime constants — the browser-safe entry point.
 *
 * Imported as `@maria-cavalheiro-website/shared-types/config`.
 *
 * CRITICAL: no import in this module reaches Zod. Every type import is erased at
 * compile time, and the one value re-export below (`./poem-content.ts`) is itself
 * held to the same rule. That is not a size optimisation alone: Zod's
 * `$ZodObjectJIT` probes `new Function("")` to decide whether it can compile a
 * fast-path validator, and `script-src 'self'` blocks that probe. Zod degrades
 * gracefully, but the violation is real and visible. Schema validation of these
 * constants happens where it belongs — in the unit suite and in
 * `scripts/prerender.ts`, both hard CI gates.
 *
 * Do not add a Zod-carrying import to this file or to anything it re-exports.
 */

// Poem vocabulary (themes, icons, scenes, collections) and the pure date and
// description formatters. Zod-free by the same contract as this module.
export * from './poem-content.ts'

export const DEFAULT_LOCALE: Locale = 'pt-BR'

export const I18N_CONFIG: I18nConfig = Object.freeze({
  defaultLocale: DEFAULT_LOCALE,
  supportedLocales: [DEFAULT_LOCALE],
  textDirection: 'ltr',
})

/**
 * Single source of truth for site identity.
 *
 * `origin` is the production custom domain. Canonical URLs, OpenGraph, JSON-LD,
 * the sitemap, the share links and the footer domain all derive from it — there is
 * no second place that repeats it.
 *
 * NO TRAILING SLASH. `absoluteUrl` resolves paths against this with `new URL`, and
 * a trailing slash there would produce `//poemas/...` for absolute pathnames.
 * `site.test.ts` pins that shape.
 */
export const SITE_CONFIG: SiteConfig = Object.freeze({
  name: 'Maria Cavalheiro',
  origin: 'https://mariacavalheiro.com.br',
  tagline: 'Poemas',
  author: 'Maria Cavalheiro',
  locale: DEFAULT_LOCALE,
})

/** Maps a BCP-47 locale to the `og:locale` underscore form required by OpenGraph. */
export function toOpenGraphLocale(locale: Locale): string {
  return locale.replace('-', '_')
}
